import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  SafetyTestCategory,
  SafetyTestCategoryCreateRequest,
  SafetyTestCategoryUpdateRequest
} from '../models/safety-test.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SafetyTestCategoryService {
  private readonly apiUrl = `${environment.apiUrl}/admin/safety-test-categories`;

  constructor(private http: HttpClient) {}

  /**
   * List all categories
   */
  async listCategories(filters?: {
    organizationId?: string;
    systemOnly?: boolean;
  }): Promise<SafetyTestCategory[]> {
    let url = this.apiUrl;
    const params = new URLSearchParams();

    if (filters?.organizationId) params.append('organizationId', filters.organizationId);
    if (filters?.systemOnly) params.append('systemOnly', 'true');

    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const response = await firstValueFrom(
      this.http.get<ApiResponse<SafetyTestCategory[]>>(url)
    );

    return response.success && response.data ? response.data : [];
  }

  /**
   * Get a single category by ID
   */
  async getCategory(id: string): Promise<SafetyTestCategory | null> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<SafetyTestCategory>>(`${this.apiUrl}/${id}`)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Create a new category
   */
  async createCategory(data: SafetyTestCategoryCreateRequest): Promise<SafetyTestCategory | null> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<SafetyTestCategory>>(this.apiUrl, data)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Update a category
   */
  async updateCategory(
    id: string,
    data: SafetyTestCategoryUpdateRequest
  ): Promise<SafetyTestCategory | null> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<SafetyTestCategory>>(`${this.apiUrl}/${id}`, data)
    );

    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete a category
   */
  async deleteCategory(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
    );

    return response.success;
  }

  /**
   * Check if category has tests
   */
  async hasTests(id: string): Promise<boolean> {
    // This would ideally be a separate endpoint, but for now
    // we can check by trying to get tests with this category
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any[]>>(
          `${environment.apiUrl}/admin/safety-tests?categoryId=${id}`
        )
      );
      return !!(response.success && response.data && response.data.length > 0);
    } catch {
      return false;
    }
  }
}
